// import external modules
import { Tab, Tabs, Toolbar } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Link, useMatches } from '@tanstack/react-router';
import { useMemo } from 'react';
import { makeStyles } from 'tss-react/mui';

// import icon svgs
import {
  AccountBalanceWallet as AccountBalanceWalletIcon,
  Unarchive as CarfTransmissionsIcon,
  Send as DataTransmissionIcon,
  CollectionsBookmark as CollectionsBookmarkIcon,
  Dashboard as DashboardIcon,
  FolderOpen as FolderOpenIcon,
  GppGood as GppGoodIcon,
  ViewTimeline as TaxLotsIcon,
  Link as LinkIcon,
  People as PeopleIcon,
  InsertChart as PlanningIcon,
  MoveToInbox as ReportsIcon,
  Settings as SettingsIcon,
  AssuredWorkload as StakingIcon,
  FindInPage as TransactionsIcon,
} from '@mui/icons-material';

// relative imports
import { useAccountConfig } from '../../providers/AccountConfigProvider';
import { useAccount } from '../../providers/AccountProvider';
import useDisplayConfig from '../custom-hooks/useDisplayConfig';
import { getComponentTestKey } from '../utilities/testUtilities';

const useStyles = makeStyles()((theme) => ({
  toolbar: {
    zIndex: 10,
    minHeight: 40,
    backgroundColor: theme.whiteLabel.SecondaryToolbar.color || 'inherit',
    boxShadow: 'none',
  },
  toolbarTabs: {
    justifyContent: 'start',
    width: '100%',
  },
  tabsRoot: {
    width: '100%',
    minHeight: 48,
  },
  tabHorizontal: {
    minHeight: 48,
    '&.Mui-selected': {
      color:
        theme.whiteLabel.SecondaryToolbar.selectedTextColor ||
        theme.palette.common.white,
    },
    color:
      theme.whiteLabel.SecondaryToolbar.textColor || 'rgba(255, 255, 255, 0.7)',
    '&:focus': {
      outline: 'none',
    },
    '&:hover': {
      paddingTop: 2,
      borderBottomWidth: 2,
      borderBottomColor: alpha(
        theme.whiteLabel.HeaderText.color || 'rgba(255, 255, 255)',
        0.7,
      ),
      borderBottomStyle: 'solid',
    },
    textTransform: 'none',
    minWidth: 'auto',
    padding: theme.spacing(0, 2),
    fontSize: '0.85rem',
  },
  tabHorizontalEnd: {
    '&.Mui-selected': {
      color:
        theme.whiteLabel.SecondaryToolbar.selectedTextColor ||
        theme.palette.common.white,
    },
    color:
      theme.whiteLabel.SecondaryToolbar.textColor || 'rgba(255, 255, 255, 0.7)',
    '&:focus': {
      outline: 'none',
    },
    '&:hover': {
      borderBottomWidth: 2,
      borderBottomColor: alpha(
        theme.whiteLabel.HeaderText.color || 'rgb(255, 255, 255)',
        0.7,
      ),
      borderBottomStyle: 'solid',
      '&.MuiTab-root': {
        paddingTop: 2,
      },
    },
    textTransform: 'none',
    minWidth: 'auto',
    marginLeft: 'auto',
  },
  indicator: {
    backgroundColor:
      theme.whiteLabel.Indicator.color || theme.palette.common.white,
    height: theme.whiteLabel.Indicator.height || 2,
  },
  tabIcon: {
    fontSize: '1.15rem',
  },
}));

export const appTabs = ({
  audit,
  accountId,
  tabIconClass,
  monitoringActive,
  tabLabels,
  isAdminViewingClient,
}) => [
  {
    id: 'dashboard',
    label: tabLabels?.dashboard || 'Overview',
    icon: <DashboardIcon className={tabIconClass} />,
    permission: false,
    matchRoutes: [
      '/_authenticated/',
      '/_authenticated/client/$accountId/_clientWrapper/',
    ],
    link: isAdminViewingClient ? `/client/${accountId}` : `/`,
  },
  {
    id: 'crypto-sources',
    label: 'Crypto Sources',
    icon: <AccountBalanceWalletIcon className={tabIconClass} />,
    permission: 'sourceView',
    matchRoutes: [
      '/_authenticated/(sources)/sources/',
      '/_authenticated/(sources)/sources/add/',
      '/_authenticated/(sources)/exchanges',
      '/_authenticated/(sources)/wallets',
      '/_authenticated/(sources)/wallets/$walletId/_wallet/summary',
      '/_authenticated/(sources)/wallets/$walletId/_wallet/addresses',
      '/_authenticated/(sources)/wallets/$walletId/_wallet/imports',
      '/_authenticated/(sources)/wallets/$walletId/_wallet/reconciliation',
      '/_authenticated/(sources)/wallets/$walletId/_wallet/defi',
      '/_authenticated/(sources)/wallets/$walletId/_wallet/settings',
      '/_authenticated/(sources)/exchanges/$exchangeId/_exchange/summary',
      '/_authenticated/(sources)/exchanges/$exchangeId/_exchange/reconciliation',
      '/_authenticated/(sources)/exchanges/$exchangeId/_exchange/imports',
      '/_authenticated/(sources)/exchanges/$exchangeId/_exchange/settings',
      // CLIENT ROUTES
      '/_authenticated/client/$accountId/_clientWrapper/(sources)/sources/',
      '/_authenticated/client/$accountId/_clientWrapper/(sources)/sources/add/',
      '/_authenticated/client/$accountId/_clientWrapper/(sources)/exchanges',
      '/_authenticated/client/$accountId/_clientWrapper/(sources)/wallets',
      '/_authenticated/client/$accountId/_clientWrapper/(sources)/wallets/$walletId/_wallet/summary',
      '/_authenticated/client/$accountId/_clientWrapper/(sources)/wallets/$walletId/_wallet/addresses',
      '/_authenticated/client/$accountId/_clientWrapper/(sources)/wallets/$walletId/_wallet/imports',
      '/_authenticated/client/$accountId/_clientWrapper/(sources)/wallets/$walletId/_wallet/reconciliation',
      '/_authenticated/client/$accountId/_clientWrapper/(sources)/wallets/$walletId/_wallet/defi',
      '/_authenticated/client/$accountId/_clientWrapper/(sources)/wallets/$walletId/_wallet/settings',
      '/_authenticated/client/$accountId/_clientWrapper/(sources)/exchanges/$exchangeId/_exchange/summary',
      '/_authenticated/client/$accountId/_clientWrapper/(sources)/exchanges/$exchangeId/_exchange/reconciliation',
      '/_authenticated/client/$accountId/_clientWrapper/(sources)/exchanges/$exchangeId/_exchange/imports',
      '/_authenticated/client/$accountId/_clientWrapper/(sources)/exchanges/$exchangeId/_exchange/settings',
    ],
    link: isAdminViewingClient ? `/client/${accountId}/sources` : `/sources`,
  },
  {
    id: 'clients',
    label: tabLabels?.clients || 'Clients',
    icon: <PeopleIcon className={tabIconClass} />,
    permission: false,
    matchRoutes: [
      '/_authenticated/clients',
    ],
    link: isAdminViewingClient ? `/client/${accountId}/clients` : `/clients`,
  },
  {
    id: 'file-management',
    label: tabLabels?.['file-management'] || 'Files',
    icon: <FolderOpenIcon className={tabIconClass} />,
    permission: false,
    matchRoutes: [
      '/_authenticated/files',
      '/_authenticated/files/management',
      '/_authenticated/files/corrections',
    ],
    link: isAdminViewingClient ? `/client/${accountId}/files` : `/files`,
  },
  {
    id: 'reports',
    label: tabLabels?.reports || 'Reports',
    icon: <ReportsIcon className={tabIconClass} />,
    permission: 'reportsView',
    matchRoutes: [
      '/_authenticated/reports',
      '/_authenticated/client/$accountId/_clientWrapper/reports',
    ],
    link: isAdminViewingClient ? `/client/${accountId}/reports` : `/reports`,
  },
  {
    id: 'submissions',
    label: tabLabels?.submissions || 'Submissions',
    icon: <CarfTransmissionsIcon className={tabIconClass} />,
    permission: false,
    matchRoutes: [
      '/_authenticated/submissions',
      '/_authenticated/submissions/$submissionId',
    ],
    link: `/submissions`,
  },
  {
    id: 'data-transmission',
    label: tabLabels?.['data-transmission'] || 'Data Transmission',
    icon: <DataTransmissionIcon className={tabIconClass} />,
    permission: false,
    matchRoutes: ['/_authenticated/transmission'],
    link: `/transmission`,
  },
  {
    id: 'integrations',
    label: tabLabels?.integrations || 'Integrations',
    icon: <LinkIcon className={tabIconClass} />,
    permission: 'integrationsView',
    matchRoutes: [
      '/_authenticated/integrations/',
      '/_authenticated/client/$accountId/_clientWrapper/integrations/',
      '/_authenticated/integrations/$integrationId',
      '/_authenticated/client/$accountId/_clientWrapper/integrations/$integrationId',
    ],
    link: isAdminViewingClient
      ? `/client/${accountId}/integrations`
      : `/integrations`,
  },
  {
    id: 'contacts',
    label: tabLabels?.contacts || 'Contacts',
    icon: <CollectionsBookmarkIcon className={tabIconClass} />,
    permission: 'contactView',
    matchRoutes: [
      '/_authenticated/contacts',
      '/_authenticated/client/$accountId/_clientWrapper/contacts',
    ],
    link: isAdminViewingClient ? `/client/${accountId}/contacts` : `/contacts`,
  },
  {
    id: 'transactions',
    label: tabLabels?.transactions || 'Transactions',
    icon: <TransactionsIcon className={tabIconClass} />,
    permission: 'txSearchView',
    matchRoutes: [
      '/_authenticated/transactions',
      '/_authenticated/client/$accountId/_clientWrapper/transactions',
    ],
    link: isAdminViewingClient
      ? `/client/${accountId}/transactions`
      : `/transactions`,
    hidden: audit,
  },
  {
    id: 'tax-lots',
    label: tabLabels?.['tax-lots'] || 'Tax Lots',
    icon: <TaxLotsIcon className={tabIconClass} />,
    permission: 'txSearchView',
    matchRoutes: [
      '/_authenticated/tax-lots',
      '/_authenticated/client/$accountId/_clientWrapper/tax-lots',
    ],
    link: isAdminViewingClient
      ? `/client/${accountId}/tax-lots`
      : `/tax-lots`,
    hidden: audit,
  },
  {
    id: 'monitoring',
    label: tabLabels?.monitoring || 'Monitoring',
    icon: <GppGoodIcon className={tabIconClass} />,
    permission: false,
    matchRoutes: [
      '/_authenticated/monitoring',
      '/_authenticated/client/$accountId/_clientWrapper/monitoring',
    ],
    link: isAdminViewingClient
      ? `/client/${accountId}/monitoring`
      : `/monitoring`,
    hidden: !monitoringActive,
  },
  {
    id: 'planning',
    label: tabLabels?.planning || 'Planning',
    icon: <PlanningIcon className={tabIconClass} />,
    permission: false,
    matchRoutes: [
      '/_authenticated/planning',
      '/_authenticated/client/$accountId/_clientWrapper/planning',
    ],
    link: isAdminViewingClient ? `/client/${accountId}/planning` : `/planning`,
  },
  {
    id: 'staking',
    label: tabLabels?.staking || 'Staking',
    icon: <StakingIcon className={tabIconClass} />,
    permission: false,
    matchRoutes: [
      '/_authenticated/staking',
      '/_authenticated/staking/$validatorId',
      '/_authenticated/client/$accountId/_clientWrapper/staking',
      '/_authenticated/client/$accountId/_clientWrapper/staking/$validatorId',
    ],
    link: isAdminViewingClient ? `/client/${accountId}/staking` : `/staking`,
  },
  {
    id: 'account-settings',
    label: tabLabels?.['account-settings'] || 'Settings',
    icon: <SettingsIcon className={tabIconClass} />,
    permission: 'accountSettingsView',
    matchRoutes: [
      '/_authenticated/settings',
      '/_authenticated/settings/general',
      '/_authenticated/settings/team',
      '/_authenticated/settings/billing',
      '/_authenticated/settings/logs',
      '/_authenticated/client/$accountId/_clientWrapper/settings',
      '/_authenticated/client/$accountId/_clientWrapper/settings/general',
      '/_authenticated/client/$accountId/_clientWrapper/settings/team',
      '/_authenticated/client/$accountId/_clientWrapper/settings/billing',
      '/_authenticated/client/$accountId/_clientWrapper/settings/logs',
    ],
    link: isAdminViewingClient
      ? `/client/${accountId}/settings/general`
      : `/settings/general`,
  },
];

const SecondaryToolbar = () => {
  const matches = useMatches();
  const { currentAccount, clientPermissions: permissions } = useAccount();
  const { accountConfig } = useAccountConfig();
  const { tabLabels } = useDisplayConfig(accountConfig);
  const { audit, chainalysisIntegrationActive, chainalysisIntegrationEnabled } =
    currentAccount;
  const { classes, cx } = useStyles();

  // generate array from appTabs and filter out any tabs with hidden property based on
  // any conditions applied, can pass args to appTabs
  const tabsToRender = useMemo(() => {
    const tabSet = accountConfig.enabledPageIds;

    const renderSet = appTabs({
      audit,
      monitoringActive:
        chainalysisIntegrationEnabled && chainalysisIntegrationActive,
      tabIconClass: classes.tabIcon,
      tabLabels,
      accountId: currentAccount.id,
      isAdminViewingClient: permissions?.isAdminViewingClient,
    }).filter((tab) => tabSet.includes(tab.id) && !tab.hidden);

    return renderSet;
  }, [
    accountConfig.enabledPageIds,
    audit,
    chainalysisIntegrationEnabled,
    chainalysisIntegrationActive,
    classes.tabIcon,
    tabLabels,
    currentAccount.id,
    permissions?.isAdminViewingClient,
  ]);

  const activeTabId = useMemo(() => {
    const lastMatchRouteId = matches[matches.length - 1].routeId;
    const matchingTab = tabsToRender.find(({ matchRoutes }) =>
      matchRoutes.some((path) => path.includes(lastMatchRouteId)),
    );
    return matchingTab?.id || false;
  }, [matches, tabsToRender]);

  return (
    <Toolbar className={classes.toolbar} variant="dense" disableGutters>
      <Tabs
        value={activeTabId}
        classes={{
          root: classes.tabsRoot,
          flexContainer: classes.toolbarTabs,
        }}
        TabIndicatorProps={{
          className: classes.indicator,
        }}
      >
        {tabsToRender.map((tab) => (
          <Tab
            key={tab.id}
            component={Link}
            className={`${
              tab.permission !== 'accountSettingsView'
                ? classes.tabHorizontal
                : cx(classes.tabHorizontal, classes.tabHorizontalEnd)
            }`}
            icon={<span>{tab.icon}</span>}
            iconPosition="start"
            label={tab.label}
            value={tab.id}
            to={tab.link}
            disabled={tab.permission && !permissions[tab.permission]}
            data-cy={getComponentTestKey({
              componentName: 'SecondaryToolbar',
              elementType: 'tab',
              elementName: tab.id,
            })}
          />
        ))}
      </Tabs>
    </Toolbar>
  );
};

export default SecondaryToolbar;
